import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { useReportsUniverse } from '~Components/ReportsUniverse';
import { useButton, useCurrency, useI18n, useSelector } from '~Hooks';
import { useCashUniverse } from '~Components/CashUniverse';
import { handyBreakpoint } from '~Styles/breakpoints';
import { useTableUniverse } from '~Components/TableUniverse';
import { useStorage } from '~Components/Storage';
import { PARKED_ORDER_TABLE_ID } from '~Scenes/ParkedOrders/consts';
import { BlackButton, BlueButton, Box } from '~Styles';
import { DateTime } from 'luxon';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { ActivityStatBox, BoxWrapper, StatBox, StyledBox } from '../styles';
import { GetItems_venue_cashSources } from '../api/__queries__';
import CheckIconSrc from '~Assets/icons/check.svg';
import Icon from '~ComponentsImproved/Icon';

const Wrapper = styled(Box)`
  margin-bottom: auto;
  max-width: 880px;

  ${handyBreakpoint} {
    flex-direction: column-reverse;
  }
`;

export const EnterEODButton = styled(BlackButton)`
  text-transform: none;
`;

export const PrintZReportButton = styled(BlueButton)`
  text-transform: none;
`;

interface Props {
  enteredCashOut: number;
  printingReport: boolean;
  tenders: GetItems_venue_cashSources[];
  cashAmountEntered: boolean;
  handleEnterEOD: () => void;
  handleFinish: () => void;
}

const SalesInfo: React.FunctionComponent<Props> = ({
  enteredCashOut,
  printingReport,
  tenders,
  cashAmountEntered,
  handleEnterEOD,
  handleFinish,
}) => {
  const { i18n } = useI18n();

  const reportsUniverse = useReportsUniverse();
  const cashUniverse = useCashUniverse();
  const currency = useCurrency();
  const tableUniverse = useTableUniverse();
  const [storage] = useStorage();

  const country = useSelector((state) => state.country);
  const hasIndividualEOD = useSelector((state) => state.hasIndividualEOD);
  const ordersNotSynced = useSelector((state) => state.POSOrdersForSync);

  const shouldNotEnterCash = hasIndividualEOD && storage.disableCash;

  const todaysDate = DateTime.now().toLocaleString({
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const todaysData = [
    { name: i18n('EOD.GrossSales'), amount: reportsUniverse.totalSales },
    {
      name: i18n('EOD.NetSales'),
      amount: reportsUniverse.totalSales - reportsUniverse.vatTotalAmount,
    },
    { name: i18n('EOD.Tips'), amount: reportsUniverse.totalTips },
    { name: i18n('EOD.Discount'), amount: reportsUniverse.discountsTotal },
  ];

  const cardsStatistic = Object.keys(reportsUniverse.paymentMethodTotals || {}).map((card) => ({
    name: card,
    amount: reportsUniverse.paymentMethodTotals[card].amount,
    currency: true,
  }));

  const tendersStatistic = tenders.map((tender) => {
    const obj = cashUniverse.sources.find((source) => source.id === tender.id) || {
      added: 0,
      removed: 0,
    };
    const x = obj?.added - obj?.removed;
    return {
      name: tender.name,
      amount: x || 0,
      currency: true,
    };
  });

  // Get the configured payment methods
  const paymentMethods = useSelector((state) =>
    state.POSPaymentMethods.sort((a, b) => a.title.localeCompare(b.title)),
  );

  const isCashActivated = !!paymentMethods.find((card) => card.id === 'CASH');

  const categoryTotalsCount = Object.keys(reportsUniverse.categoryTotals).reduce(
    (accum, itemKey) => accum + reportsUniverse.categoryTotals[itemKey].count,
    0,
  );

  const paymentStats = [...cardsStatistic, ...tendersStatistic];

  const activityStats = [
    { name: i18n('EOD.Transactions'), amount: reportsUniverse.numberOfOrders, currency: false },
    { name: i18n('EOD.Refunds'), amount: reportsUniverse.totalRefunds?.amount, currency: true },
    { name: i18n('EOD.NOfSoldArticles'), amount: categoryTotalsCount, currency: false },
  ];

  const isZreportActive =
    (isCashActivated && cashAmountEntered) ||
    (isCashActivated && cashAmountEntered) ||
    !isCashActivated ||
    !isCashActivated;

  const disableEODBasedOnOpenTables =
    country === 'NO' || country === 'ES'
      ? tableUniverse?.cheques
          // Filter out cheques created in the POS mode in handy
          .filter((c) => !c.tableId?.startsWith('POS_'))
          .filter((c) => c.tableId !== PARKED_ORDER_TABLE_ID) // Filter out cheques that is parked orders
          .some((c) => c.tableId)
      : false;

  const cashEntryDisabledReasons = useMemo(() => {
    return {
      dayNotStarted: {
        condition: !reportsUniverse.dayStarted,
        message: i18n('EOD.EnterCashError.DayNotStarted'),
      },
      openTables: {
        condition: disableEODBasedOnOpenTables,
        message: i18n('EOD.EnterCashError.OpenTables'),
      },
      deviceSpecificDisable: {
        condition: shouldNotEnterCash,
        message: i18n('EOD.EnterCashError.DeviceSpecific'),
      },
      ordersNotSynced: {
        condition: ordersNotSynced.length > 0,
        message: i18n('EOD.EnterCashError.OrdersNotSynced'),
      },
    };
  }, [
    reportsUniverse.dayStarted,
    disableEODBasedOnOpenTables,
    shouldNotEnterCash,
    ordersNotSynced,
    i18n,
  ]);

  const enterEODButton = useButton({
    onClick: () => handleEnterEOD(),
    disabled: !reportsUniverse.dayStarted || disableEODBasedOnOpenTables || shouldNotEnterCash,
  });

  const printZReportButton = useButton({
    onClick: () => handleFinish(),
    disabled: !isZreportActive || printingReport,
  });

  return (
    <Wrapper display="flex" gap="small" justifyContent="space-between">
      <BoxWrapper display="flex" flexDirection="column" gap="small">
        <StyledBox padding="medium" display="flex" flexDirection="column" gap="xs">
          <Heading level={3}>{todaysDate}</Heading>
          <Box>
            {todaysData.map(({ name, amount }) => (
              <StatBox display="flex" justifyContent="space-between" key={name}>
                <Paragraph level={4} color="var(--colorBlueGray1200)">
                  {name}
                </Paragraph>
                <Heading level={4}>{amount ? currency.format(amount, true) : '-'}</Heading>
              </StatBox>
            ))}
          </Box>

          {isZreportActive && (
            <Box>
              <Box display="flex" justifyContent="space-between">
                <Heading level={4}>{i18n('EOD.EndOfDay')}</Heading>
                <Heading level={4}>{currency.format(enteredCashOut, true)}</Heading>
              </Box>
            </Box>
          )}
        </StyledBox>

        <StyledBox padding="medium" display="flex" flexDirection="column" gap="xs">
          <Heading level={3}>{i18n('EOD.Payments')}</Heading>
          <Box>
            {paymentStats.map((item) => {
              const checkValue =
                item.amount && item.currency
                  ? currency.format(item.amount, true)
                  : item.amount || '-';
              return (
                <StatBox key={item.name} display="flex" justifyContent="space-between">
                  <Paragraph level={4} color="var(--colorBlueGray1200)">
                    {item.name}
                  </Paragraph>
                  <Heading level={4}>{checkValue}</Heading>
                </StatBox>
              );
            })}
          </Box>
        </StyledBox>

        <StyledBox padding="medium" display="flex" flexDirection="column" gap="xs">
          <Heading level={3}>{i18n('EOD.Activity')}</Heading>
          <Box display="flex" justifyContent="space-between" gap="small">
            {activityStats.map((item) => {
              const checkValue =
                item.amount && item.currency
                  ? currency.format(item.amount, true)
                  : item.amount || '-';
              return (
                <ActivityStatBox
                  key={item.name}
                  fluid
                  padding="small"
                  display="flex"
                  flexDirection="column"
                  gap="xs"
                  alignItems="center">
                  <Heading level={3}>{checkValue}</Heading>
                  <Paragraph level={4} color="var(--colorBlueGray1200)">
                    {item.name}
                  </Paragraph>
                </ActivityStatBox>
              );
            })}
          </Box>
        </StyledBox>
      </BoxWrapper>

      <BoxWrapper display="flex" flexDirection="column" gap="medium">
        <StyledBox
          padding="medium"
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          gap="medium">
          <Box display="flex" flexDirection="column" gap="small">
            <Heading level={3}>{i18n('EOD.EndOfDay')}</Heading>
            <Paragraph level={4} color="var(--colorBlueGray1200)">
              {i18n('EOD.EndOfDay.Procedure.Subtitle')}
            </Paragraph>
          </Box>

          {isCashActivated && (
            <Box display="flex" flexDirection="column" gap="small">
              <EnterEODButton display="flex" alignItems="center" gap="xs" {...enterEODButton}>
                {i18n('EOD.EnterEODBalance')}
                {(cashAmountEntered || shouldNotEnterCash) && (
                  <Icon src={CheckIconSrc} className="icon check" />
                )}
              </EnterEODButton>
              {Object.entries(cashEntryDisabledReasons).map(([key, { condition, message }]) => {
                if (condition) {
                  return (
                    <Paragraph key={key} level={4} color="var(--colorUIOrange)">
                      • {message}
                    </Paragraph>
                  );
                }
                return null;
              })}
            </Box>
          )}

          <PrintZReportButton {...printZReportButton}>
            {i18n('EOD.PrintZReport')}
          </PrintZReportButton>
        </StyledBox>
      </BoxWrapper>
    </Wrapper>
  );
};

export default SalesInfo;
